import {originalChannels} from "@hosttools/core/constant";
import {
    AddIcon,
    Button,
    ButtonIcon,
    ButtonText,
    ChannelLogo,
    Divider,
    ExternalIcon,
    HStack,
    Modal,
    Text,
    useChannelData,
    VStack
} from "@hosttools/frontend";
import {type AccountType} from "@hosttools/frontend/models/account";
import {type Listing} from "@hosttools/frontend/models/listing";
import {buildChannelConnectorURL} from "@hosttools/frontend/utils/account";
import {type FC, memo, useCallback, useMemo, useState} from "react";
import {useHistory, useRouteMatch} from "react-router-dom";

import ChannelItem from "./ChannelItem";

import ModalImportListings from "@/client/features/AddListing/ImportListings/ModalImportListings";

interface Props {
    listing: Listing;
    onClose: () => void;
    onToggleChannel?: (listing: Listing, accountType: AccountType, isConnected: boolean) => void;
}

const ModalConnectChannels: FC<Props> = ({listing, onClose, onToggleChannel}) => {
    const history = useHistory();
    const {url} = useRouteMatch();
    const {directBookingIDs} = useChannelData(listing._id, "direct") ?? {};

    const policy =
        "Host Tools will use your API key to automatically send messages to your guests or set your listing's prices and availability on your behalf.";

    const [selectedChannel, setSelectedChannel] = useState<AccountType>();
    const [addAccountType, setAddAccountType] = useState<AccountType>();

    const connectUrlMaker = useCallback(
        (type: AccountType) => {
            if (type === "ChannelConnector") {
                return `/settings/listings/${listing._id}/channel-connector`;
            }
            return `/add-listing?listingID=${listing._id}&returnUrl=${url}`;
        },
        [url, listing]
    );

    const returnUrl = useMemo(() => {
        if (!addAccountType) {
            return;
        }
        // default will redirect to connect page with current page as return url
        return connectUrlMaker(addAccountType);
    }, [addAccountType, connectUrlMaker]);

    const handleToggleChannel = useCallback(
        async (listing: Listing, accountType: AccountType, isConnected: boolean) => {
            if (accountType === "ChannelConnector" && isConnected) {
                const url = buildChannelConnectorURL(listing.channels.ChannelConnector.accountID);
                history.push(url);
                return;
            }
            setSelectedChannel(accountType);
            if (onToggleChannel) {
                onToggleChannel(listing, accountType, isConnected);
            } else if (!isConnected) {
                // use default behavior
                const connectPath = connectUrlMaker(accountType);
                if (accountType === "ChannelConnector") {
                    history.push(connectPath);
                } else {
                    setAddAccountType(accountType);
                }
            }
        },
        [onToggleChannel, history, connectUrlMaker]
    );

    const clearState = useCallback(() => {
        setAddAccountType(undefined);
        setSelectedChannel(undefined);
    }, []);

    const handleImportListingsSuccess = useCallback(() => {
        clearState();
        onClose();
    }, [onClose, clearState]);

    const handleHideModalImportListings = useCallback(() => {
        clearState();
        onClose();
    }, [clearState, onClose]);

    const handleOpenDirectBookings = useCallback(() => {
        if (directBookingIDs?.length) {
            // open the 1st one by default
            history.push(`/settings/direct-bookings?id=${directBookingIDs[0]}`);
        } else {
            history.push(`/settings/direct-bookings?listingID=${listing._id}`);
        }
    }, [history, listing._id, directBookingIDs]);

    if (addAccountType) {
        return (
            // this would end up navigate away from our page so keep everything on return url
            // set redirect url here for Airbnb
            // call the callback after this is done
            <ModalImportListings
                accountType={addAccountType}
                listingID={listing._id}
                oAuthReturnUrl={
                    typeof ONBOARDING_OAUTH_RETURN_URL !== "undefined"
                        ? ONBOARDING_OAUTH_RETURN_URL
                        : `${window.location.origin}/add-listing`
                }
                oAuthStateParameter={returnUrl}
                onClose={handleHideModalImportListings}
                onImportListingsSuccess={handleImportListingsSuccess}
            />
        );
    }

    const divider = (
        <HStack className="items-center" space="2xl">
            <Divider className="flex-1" />
            <Text variant="md">Or</Text>
            <Divider className="flex-1" />
        </HStack>
    );

    return (
        <Modal isOpen subTitle={policy} title="Connect Channels" onClose={onClose}>
            <VStack space="2xl">
                <VStack className="border border-solid border-blueGray-200 rounded-md">
                    {originalChannels.map(elem => (
                        <ChannelItem
                            key={elem}
                            accountType={elem}
                            className="first:border-t-0 last:border-b-0 border-b border-solid border-blueGray-200"
                            isConnected={!!listing.channels[elem]}
                            isDisabled={!!listing.channels[elem]}
                            isLoading={selectedChannel === elem}
                            listing={listing}
                            onToggleChannel={handleToggleChannel}
                        />
                    ))}
                    <HStack className="justify-between px-4 py-4">
                        <HStack className="items-center" space="lg">
                            <ChannelLogo channel="direct" size="2xl" />
                            <Text variant="md">Direct Booking</Text>
                        </HStack>
                        <Button variant="outline" onPress={handleOpenDirectBookings}>
                            <ButtonIcon as={directBookingIDs?.length ? ExternalIcon : AddIcon} />
                            <ButtonText>
                                {directBookingIDs?.length
                                    ? "Open Direct Bookings"
                                    : "Create custom site"}
                            </ButtonText>
                        </Button>
                    </HStack>
                </VStack>
                {__IS_CC_ENABLED__ && (
                    <>
                        {divider}
                        <ChannelItem
                            accountType="ChannelConnector"
                            className="border border-solid border-blueGray-200 rounded-md bg-blueGray-50"
                            isConnected={!!listing.channels.ChannelConnector}
                            isLoading={selectedChannel === "ChannelConnector"}
                            listing={listing}
                            onToggleChannel={handleToggleChannel}
                        />
                    </>
                )}
            </VStack>
        </Modal>
    );
};

export default memo(ModalConnectChannels);
